import type { TextDepth } from '../../types';
import { getBevelDepth, BevelRenderSettings } from './getBevelDepth';

export type DepthRenderSettings = {
  dx: number;
  dy: number;
  softness: number;
  opacity: number;
  bevel?: BevelRenderSettings;
};

// Rendering-proof contract: values are in document pixels and degrees clockwise
// from above. Keep this reader identical in Studio and Page Generator.
export function getTextDepth(depth?: TextDepth): DepthRenderSettings | null {
  if (
    depth?.version === 2 &&
    (depth.mode === 'raised' || depth.mode === 'recessed')
  ) {
    const bevel = getBevelDepth(depth);
    return bevel
      ? { dx: 0, dy: 0, softness: bevel.softness, opacity: 1, bevel }
      : null;
  }
  if (
    !depth ||
    depth.version !== 1 ||
    (depth.mode !== 'raised' && depth.mode !== 'recessed') ||
    ![depth.size, depth.lightDirection, depth.softness, depth.strength].every(
      (value) => typeof value === 'number' && Number.isFinite(value)
    )
  ) {
    return null;
  }
  const size = Math.min(8, Math.max(0, depth.size));
  const strength = Math.min(100, Math.max(0, depth.strength));
  if (size === 0 || strength === 0) {
    return null;
  }
  const angle = ((depth.lightDirection % 360) * Math.PI) / 180;
  const direction = depth.mode === 'raised' ? 1 : -1;
  return {
    dx: -Math.sin(angle) * size * direction,
    dy: Math.cos(angle) * size * direction,
    softness: Math.min(4, Math.max(0, depth.softness)),
    opacity: strength / 100,
  };
}

// Scale only painted lengths. Angular, percentage and contour values are unitless.
export function scaleTextDepth(
  depth: DepthRenderSettings,
  x: number,
  y = x
): DepthRenderSettings {
  return {
    ...depth,
    dx: depth.dx * x,
    dy: depth.dy * y,
    softness: depth.softness * y,
    ...(depth.bevel
      ? {
          bevel: {
            ...depth.bevel,
            size: depth.bevel.size * x,
            softness: depth.bevel.softness * y,
            surfaceScale: depth.bevel.surfaceScale * y,
            renderScale: depth.bevel.renderScale * y,
          },
        }
      : {}),
  };
}
